import {
  Component,
  ElementRef,
  inject,
  ViewChild,
  AfterViewInit,
  model,
  input,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SectionLockDirective } from '@shared/directives/section-lock.directive';
import { ProbateSections } from '@app/core/probate/models/probate-sections.models';
import { getImageBlobFromUrl } from '@shared/utils';
import { CcsInputComponent } from '@shared/components/ccs-input/ccs-input.component';
import { CcsSelectComponent } from '@shared/components/ccs-select/ccs-select.component';

@Component({
  selector: 'app-signature-typed-text',
  imports: [
    CcsInputComponent,
    CcsSelectComponent,
    ReactiveFormsModule,
    SectionLockDirective,
  ],
  templateUrl: './signature-typed-text.component.html',
})
export class SignatureTypedTextComponent implements AfterViewInit {
  private _fb = inject(FormBuilder);

  areSignatureTabsPristine = model<boolean>();

  isDisabled = input<boolean>(false);

  readonly Section = ProbateSections;

  typedSignatureForm = this._fb.group({
    type_name: this._fb.control<string>({
      value: '',
      disabled: this.isDisabled(),
    }),
    font: this._fb.control<string>('Aguafina Script'),
  });

  readonly fontsOptions = [
    { label: 'Aguafina Script', value: 'Aguafina Script' },
    { label: 'Alex Brush', value: 'Alex Brush' },
    { label: 'Allura', value: 'Allura' },
    { label: 'Arizonia', value: 'Arizonia' },
    { label: 'Baroque Antique Script', value: 'Baroque Antique Script' },
    { label: 'Chopin Script', value: 'Chopin Script' },
    { label: 'Damion', value: 'Damion' },
    { label: 'Dancing Script', value: 'Dancing Script' },
    { label: 'Grand Hotel', value: 'Grand Hotel' },
    { label: 'Kaushan Script', value: 'Kaushan Script' },
    { label: 'Little Days', value: 'Little Days' },
    { label: 'Lobster', value: 'Lobster' },
    { label: 'Playball', value: 'Playball' },
    { label: 'Precious', value: 'Precious' },
    { label: 'Rochester', value: 'Rochester' },
    { label: 'Sacramento', value: 'Sacramento' },
    { label: 'Scriptin', value: 'Scriptin' },
    { label: 'Sofia', value: 'Sofia' },
  ];

  private readonly _fontSize = 24;
  private readonly _textColor = '#000000';

  @ViewChild('textCanvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  private _canvas!: HTMLCanvasElement;
  private _ctx!: CanvasRenderingContext2D;

  ngAfterViewInit() {
    this._canvas = this.canvasRef.nativeElement;
    this._ctx = this._canvas.getContext('2d')!;
    this._initializeCanvas();

    this.typedSignatureForm.valueChanges.subscribe(() => {
      this._drawText();
      const isTypeTextPristine =
        this.typedSignatureForm.get('type_name')?.value === '';
      if (isTypeTextPristine) {
        this.areSignatureTabsPristine.set(true);
      } else {
        this.areSignatureTabsPristine.set(false);
      }
    });
  }
  private _initializeCanvas() {
    const dpr = window.devicePixelRatio || 1;

    const displayWidth = this._canvas.clientWidth || 600;
    const displayHeight = this._canvas.clientHeight || 200;

    this._canvas.width = Math.floor(displayWidth * dpr);
    this._canvas.height = Math.floor(displayHeight * dpr);

    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this._ctx.fillStyle = 'white';
    this._ctx.fillRect(0, 0, displayWidth, displayHeight);
  }

  private async _drawText() {
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = this._canvas.width / dpr;
    const displayHeight = this._canvas.height / dpr;

    // Clear background using display-size coords
    this._ctx.fillStyle = 'white';
    this._ctx.fillRect(0, 0, displayWidth, displayHeight);

    const textValue = this.typedSignatureForm.value.type_name || '';
    const selectedFont = this.typedSignatureForm.value.font || 'Arial';
    if (textValue.trim()) {
      await document.fonts.load(`${this._fontSize}px "${selectedFont}"`);

      this._ctx.font = `${this._fontSize}px ${selectedFont}`;
      this._ctx.fillStyle = this._textColor;
      this._ctx.textAlign = 'center';
      this._ctx.textBaseline = 'middle';

      // Center using display-size coords
      const centerX = displayWidth / 2;
      const centerY = displayHeight / 2;

      // Handle multiline text
      const lines = textValue.split('\n');
      const lineHeight = this._fontSize * 1.2;
      const startY = centerY - ((lines.length - 1) * lineHeight) / 2;

      lines.forEach((line, index) => {
        this._ctx.fillText(line, centerX, startY + index * lineHeight);
      });
    }
  }

  getImageBlob(): Blob {
    const dataUrl = this._canvas.toDataURL('image/png');
    const imageBlob = getImageBlobFromUrl(dataUrl);
    return imageBlob;
  }

  private _resetForm() {
    this.typedSignatureForm.reset();
  }

  resetAll() {
    this._resetForm();
    this._initializeCanvas();
  }
}
